import type { TenantBundle } from "@crosure/tenant-runtime";

/**
 * Visual design tokens for Green Shield Insurance Brokers.
 *
 * These feed both Chakra UI theme overrides and CSS variables in the
 * skeleton — keep them as plain strings so both consumers can read them
 * without conversion logic.
 *
 * Every value below is Green Shield's real brand palette, read from the live
 * site's own custom properties (the `:root` block inlined in
 * greenshieldbrokers.com/index.php). It previously carried the scaffolded
 * placeholder — `#2563eb` blue — which is not Green Shield's colour at all; a
 * page-builder page rendered against it looked nothing like the live site.
 *
 * The brand is TWO colours, and which is "primary" is the one judgement call
 * in this file:
 *
 *   navy  #163354   the hero background, the footer, every heading, every link
 *   green #3DAA47   the accent, the focus ring, and every primary button
 *
 * Upstream names the grey `--primary` and the green `--accent`, but upstream's
 * `--primary` is only ever used as body-text colour. In this token set
 * `primary` drives buttons and calls to action, so it is the GREEN; the navy
 * takes `accent`, which is the slot the page-builder components use for dark
 * bands and headings (the same mapping commsure-stu-v2 uses for its deep
 * purple). Swapping these two would turn every button navy and every heading
 * green.
 *
 * The same values are the fallbacks in `components/tokens.ts`, which is what
 * the page-builder components read. That duplication is deliberate: this file
 * is the Chakra/skeleton surface, that one is the BYOD component surface, and
 * both must stay legible on their own. If you change a colour here, change it
 * there too.
 *
 * The two are not the same SET, though. `ColorTokens` (see @crosure/tenant-
 * runtime) has these eleven slots; Studio's Design System publishes thirteen,
 * adding `secondary-foreground` and `muted-foreground`. Those two exist only
 * as CSS variables, so only `components/tokens.ts` can reach them — do not try
 * to add them here, the type rejects it.
 */
export const theme: TenantBundle["theme"] = {
  colors: {
    // --button-primary-background. The green is the action colour: every
    // primary button, the focus ring, the service-card numerals and the
    // compliance tick.
    primary:           "#3DAA47",
    primaryForeground: "#ffffff",
    // --secondary / --ice-grey. The pale band behind alternating sections and
    // behind the secondary button at rest. Its foreground is the navy (see
    // components/tokens.ts) — NOT white, which is what the platform default
    // for this slot pairs with it. See the warning in that file.
    secondary:         "#F2F6FA",
    // --navy. Hero background, footer background, link colour and every
    // heading. `accent` is the only free slot for it: `foreground` is body
    // copy, which upstream sets to the much lighter #607184.
    accent:            "#163354",
    background:        "#ffffff",
    // --text / --ink. A grey-blue, not a near-black: 5.0:1 on white, so it
    // clears AA for body copy, which is the only thing it is used for.
    foreground:        "#607184",
    // --ice-grey again. The same value as `secondary` on purpose, not by
    // oversight — upstream has one soft surface colour and reaches for it
    // both as a section band and as a button background.
    muted:             "#F2F6FA",
    // --border.
    border:            "#DBE4EC",
    // Semantic states. The live site has no success/warning/destructive of
    // its own, so these are the platform values, except `success` which takes
    // the brand green rather than sitting a shade away from it.
    success:           "#3DAA47",
    warning:           "#d97706",
    destructive:       "#dc2626",
  },
  fonts: {
    // Sora is the brand face for headings, navigation and buttons; Tajawal is
    // its Arabic counterpart. NEITHER is loaded by the storefront — unlike
    // commsure-stu-v2, which inherits Inter from @fontsource via the
    // skeleton's Chakra theme, this tenant's faces are its own.
    //
    // They are fetched from Google Fonts by `FONT_IMPORT_CSS` in
    // components/tokens.ts, emitted once by pageBuilder/root.tsx. See the note
    // there for why an @import rather than a bundled woff2.
    //
    // `sans` is the single family the skeleton reads for BOTH --font-heading
    // and --font-body (see lib/theme.ts in the skeleton), so it carries the
    // heading face with the body face behind it; the BYOD components split the
    // two properly through their own tokens. A font configured in Studio's
    // Design System overrides this.
    sans: {
      family:
        'Sora, Calibri, "Segoe UI", Tahoma, Arial, sans-serif',
    },
  },
  radii: {
    sm: "0.25rem",
    md: "0.5rem",
    // --card-radius: 24px. Upstream rounds every card, panel and image well at
    // this, so it is the workhorse radius of the design rather than a large
    // variant of it. (Service cards are 20px — a deliberate step down that is
    // a literal in the component's own stylesheet.)
    lg: "1.5rem",
    // --button-radius: 999px. Every button in this design is a pill.
    xl: "9999px",
  },
};
